import { z } from 'zod';

import { buildQueryString } from '../..';
import { BooleanStringSchema } from '../../common';
import { ProductLine } from '../brand/brand';
import { AVAILABLE_BRAND_FEATURES } from '../brand/settings';
import { IEndpointSchema, makeEndpoint, MinimalHostSchema, paginateResponseSchema, SortOrder } from '../common';
import { IMediaReferenceSchema } from '../media/media';
import { PromoterAgreementStatus } from '../promoter/types';
import { BrandSchema } from '../user/user';

export enum OrganizationRole {
  ADMIN = 'admin',
}

export enum ExperiencePillar {
  CULINARY = 'Culinary',
  WELLNESS = 'Wellness',
  SOCIAL_AND_SCENE = 'Social & Scene',
  CULTURAL = 'Cultural',
  FAMILY_FRIENDLY = 'Family-Friendly',
  RELAXATION_RESORT = 'Relaxation',
  EDUCATIONAL_SKILL_BUILDING = 'Educational',
  VIP_EXCLUSIVE = 'VIP & Exclusive',
  ADVENTURE_ACTIVE = 'Adventure & Active',
  ROMANTIC = 'Romantic',
  DESIGN_AND_FASHION = 'Design & Fashion',
  ARTS_AND_CREATIVITY = 'Arts & Creativity',
  FITNESS_ORIENTED = 'Fitness-Oriented',
  SPIRITUAL = 'Spiritual',
}

export const EXPERIENCE_PILLAR_DESCRIPTIONS: Record<ExperiencePillar, string> = {
  [ExperiencePillar.CULINARY]: 'Focuses on all dining, cooking, and beverage-related experiences.',
  [ExperiencePillar.WELLNESS]: 'Focuses on physical, mental, or spiritual health and recovery.',
  [ExperiencePillar.SOCIAL_AND_SCENE]: 'Focuses on creating atmosphere, music, and group connection.',
  [ExperiencePillar.CULTURAL]: 'Focuses on local history, traditions, art, and community heritage.',
  [ExperiencePillar.FAMILY_FRIENDLY]: 'Focuses on programming for children, teens, and parents together.',
  [ExperiencePillar.RELAXATION_RESORT]: 'Focuses on passive leisure, high-touch service, and pools or beaches.',
  [ExperiencePillar.EDUCATIONAL_SKILL_BUILDING]:
    'Focuses on workshops that teach a tangible skill or specialized knowledge.',
  [ExperiencePillar.VIP_EXCLUSIVE]: 'Focuses on private, bespoke, limited-access moments and discretion.',
  [ExperiencePillar.ADVENTURE_ACTIVE]: 'Focuses on sports, challenging excursions, and vigorous physical activity.',
  [ExperiencePillar.ROMANTIC]: 'Focuses on creating intimate, tailored moments for couples and celebrations.',
  [ExperiencePillar.DESIGN_AND_FASHION]: 'Focuses on aesthetic appreciation, architecture, style, and visual art.',
  [ExperiencePillar.ARTS_AND_CREATIVITY]: 'Focuses on hands-on creation, performance, or artistic expression.',
  [ExperiencePillar.FITNESS_ORIENTED]: 'Focuses on structured movement, gym classes, and physical training.',
  [ExperiencePillar.SPIRITUAL]: 'Focuses on inner peace, guided meditation, and holistic well-being.',
};

export enum BrandTone {
  REFINED = 'Refined',
  RELAXED = 'Relaxed',
  BOLD = 'Bold',
  PLAYFUL = 'Playful',
  MINIMALIST = 'Minimalist',
  ELEVATED = 'Elevated',
  EDGY = 'Edgy',
  FORMAL = 'Formal',
  TRANQUIL = 'Tranquil',
  ENERGETIC = 'Energetic',
  AUTHENTIC = 'Authentic',
  MYSTERIOUS = 'Mysterious',
  ROMANTIC = 'Romantic',
  SINCERE = 'Sincere',
  EMPOWERING = 'Empowering',
  ADVENTUROUS = 'Adventurous',
  NOSTALGIC = 'Nostalgic',
}

export const BRAND_TONE_DESCRIPTIONS: Record<BrandTone, string> = {
  [BrandTone.REFINED]: 'Voice is sophisticated and elegant; copy focuses on elevated language, quality, and detail.',
  [BrandTone.RELAXED]:
    'Voice is easygoing and stress-free; copy emphasizes comfort, simplicity, and low-pressure enjoyment.',
  [BrandTone.BOLD]: 'Voice is striking, high-impact, and confident; copy uses strong verbs to drive attention.',
  [BrandTone.PLAYFUL]: 'Voice is lighthearted, witty, and fun; copy uses humor and unexpected analogies.',
  [BrandTone.MINIMALIST]: 'Voice is clean, spare, and direct; copy focuses on brevity and essential, high-quality elements.',
  [BrandTone.ELEVATED]: 'Voice is inspiring and aspirational; copy conveys luxury, prestige, and superior quality.',
  [BrandTone.EDGY]:
    'Voice is sharp, unconventional, and provocative; copy challenges expectations and uses unusual framing.',
  [BrandTone.FORMAL]: 'Voice is traditional, structured, and respectful; copy emphasizes order and established quality.',
  [BrandTone.TRANQUIL]:
    'Voice is soothing, quiet, and calming; copy emphasizes stillness, peace, and restorative sensory detail.',
  [BrandTone.ENERGETIC]:
    'Voice is dynamic, fast-paced, and lively; copy uses active voice and vivid descriptors for excitement.',
  [BrandTone.AUTHENTIC]:
    'Voice is genuine, transparent, and grounded; copy emphasizes truth, local connection, and sincerity.',
  [BrandTone.MYSTERIOUS]: 'Voice is intriguing, veiled, and suggestive; copy builds curiosity and a sense of the unknown.',
  [BrandTone.ROMANTIC]:
    'Voice is passionate and sentimental; copy focuses on connection, intimacy, and emotional depth for couples.',
  [BrandTone.SINCERE]:
    'Voice is earnest, honest, and warm; copy emphasizes genuine care and dedication to guest well-being.',
  [BrandTone.EMPOWERING]: `Voice is motivating and affirming; copy focuses on the guest's agency and personal transformation.`,
  [BrandTone.ADVENTUROUS]:
    'Voice is exploratory, daring, and action-oriented; copy emphasizes discovery and embracing the unfamiliar.',
  [BrandTone.NOSTALGIC]: 'Voice is reflective, sentimental, and rooted in the past; copy recalls history and tradition.',
};

export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1, 'Name is required'),
  segment: z.string().nullable(),
  parentOrganizationId: z.string().uuid('Invalid organization id supplied as parent organization').nullable().optional(),
});

export type OrganizationTreeType = z.infer<typeof OrganizationSchema> & {
  userPermissions?: string[] | undefined;
  brandCount?: number | undefined;
  userCount?: number | undefined;
  segment: string | null;
  childOrganizations: OrganizationTreeType[];
};

export const OrganizationTreeSchema: z.ZodType<OrganizationTreeType> = OrganizationSchema.extend({
  brandCount: z.number().optional(),
  userCount: z.number().optional(),
  userPermissions: z.array(z.string()).optional(),
  childOrganizations: z.lazy(() => OrganizationTreeSchema.array()),
});

export type OrganizationType = z.infer<typeof OrganizationSchema>;

export const OrganizationDetailResponseSchema = OrganizationSchema.pick({
  id: true,
  name: true,
  segment: true,
  parentOrganizationId: true,
}).extend({
  products: z.array(z.nativeEnum(ProductLine)).nullable(),
  userPermissions: z.array(z.string()),
  parentOrganizationName: z.string().nullable(),
  childrenOrganizations: OrganizationTreeSchema.array().optional(),
  isTopLevel: z.boolean().optional(),
  createdById: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});

export type OrganizationDetailResponse = z.infer<typeof OrganizationDetailResponseSchema>;

export const CreateOrganizationSchema = OrganizationSchema.pick({
  name: true,
  segment: true,
  parentOrganizationId: true,
});

export const CreateOrganizationFormSchema = OrganizationSchema.pick({
  name: true,
  parentOrganizationId: true,
}).extend({
  segment: z.array(z.string().nullable()),
});

export const UpdateOrganizationSchema = OrganizationSchema.pick({
  name: true,
  segment: true,
  parentOrganizationId: true,
})
  .partial()
  .strict();

export const UpdateOrganizationFormSchema = OrganizationSchema.pick({
  name: true,
  parentOrganizationId: true,
})
  .extend({
    segment: z.array(z.string().nullable()),
  })
  .partial()
  .strict();

export const OrganizationPaginatedResponseSchema = paginateResponseSchema(
  OrganizationSchema.pick({
    id: true,
    name: true,
    segment: true,
    parentOrganizationId: true,
  }).extend({
    products: z.array(z.nativeEnum(ProductLine)).nullable(),
    brandCount: z.number(),
    userCount: z.number(),
    userPermissions: z.array(z.string()).optional(),
    childOrganizations: OrganizationTreeSchema.array(),
  }),
).strict();

export const OrganizationTreeResponseSchema = OrganizationSchema.pick({
  name: true,
  segment: true,
  parentOrganizationId: true,
}).extend({
  childOrganizations: OrganizationTreeSchema.array().optional(),
});

export const MinimalOrganizationListResponseSchema = z.array(
  OrganizationSchema.pick({
    id: true,
    name: true,
    segment: true,
    parentOrganizationId: true,
  }).extend({
    childOrganizations: OrganizationTreeSchema.array(),
  }),
);

export type MinimalOrganizationListResponse = z.infer<typeof MinimalOrganizationListResponseSchema>;

export enum OrganizationSortBy {
  NAME = 'name',
  CREATED_AT = 'createdAt',
}

export const PaginatedOrganizationsRequestQuerySchema = z
  .object({
    q: z.string().optional(),
    parentOrganizationId: z.string().uuid().optional(),
    subOrganizations: z
      .boolean()
      .or(BooleanStringSchema.transform((str) => str.toLowerCase() === 'true'))
      .optional(),
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(1).default(10),
    sortBy: z.nativeEnum(OrganizationSortBy).optional(),
    sortDir: z.nativeEnum(SortOrder).optional(),
  })
  .strict();

export const MinimalOrganizationListRequestQuerySchema = PaginatedOrganizationsRequestQuerySchema.pick({
  q: true,
  parentOrganizationId: true,
  subOrganizations: true,
});

const BooleanQuerySchema = BooleanStringSchema.transform((str) => str.toLowerCase() === 'true');
export const OrganizationsDetailRequestQuerySchema = z
  .object({
    showChildren: BooleanQuerySchema.optional(),
    showIsTopLevel: BooleanQuerySchema.optional(),
  })
  .strict();

export const OrganizationFeatureFlagsResponseSchema = z.object({
  [AVAILABLE_BRAND_FEATURES.API_ACCESS]: z.boolean(),
  [AVAILABLE_BRAND_FEATURES.AI_CURATION_2_0]: z.boolean(),
});
export type OrganizationFeatureFlagsResponse = z.infer<typeof OrganizationFeatureFlagsResponseSchema>;

export const AssignBrandToOrganizationSchema = z
  .object({
    brandIds: z.array(z.string().uuid()).min(1),
  })
  .strict();

export const AssignBrandToOrganizationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/brands`,
  responseSchema: z.void(),
  requestSchema: AssignBrandToOrganizationSchema,
});

export const RemoveBrandFromOrganizationEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ organizationId, brandId }: { organizationId: string; brandId: string }) =>
    `v1/organizations/${organizationId}/brands/${brandId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const AssignOrganizationToAnotherOrganizationSchema = z
  .object({
    organizationId: z.string().uuid(),
  })
  .strict();

export const AssignOrganizationToAnotherOrganizationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/organizations`,
  responseSchema: z.void(),
  requestSchema: AssignOrganizationToAnotherOrganizationSchema,
});

export const PaginatedOrganizationsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `v1/organizations`,
  responseSchema: OrganizationPaginatedResponseSchema,
  requestSchema: PaginatedOrganizationsRequestQuerySchema,
});

export const PaginatedMinimalOrganizationsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `v1/organizations/minimal`,
  responseSchema: MinimalOrganizationListResponseSchema,
  requestSchema: MinimalOrganizationListRequestQuerySchema,
});

export const GetOrganizationEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}`,
  responseSchema: OrganizationDetailResponseSchema,
  requestSchema: OrganizationsDetailRequestQuerySchema,
});

export const GetOrganizationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/organizations?${buildQueryString({ brandId })}`,
  responseSchema: MinimalOrganizationListResponseSchema,
  requestSchema: z.void(),
});

export const CreateOrganizationEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/organizations`,
  responseSchema: z.void(),
  requestSchema: CreateOrganizationSchema,
});

export const UpdateOrganizationEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}`,
  responseSchema: z.void(),
  requestSchema: UpdateOrganizationSchema,
});

export const DeleteOrganizationEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const MinimalOrganizationListEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `v1/organizations/minimal`,
  responseSchema: MinimalOrganizationListResponseSchema,
  requestSchema: MinimalOrganizationListRequestQuerySchema,
});

export const BrandsDashboardStatsResponseSchema = z.object({
  totalBookingAmount: z.number(),
  averageBookingAmount: z.number(),
  averageBookingWindowInMinutes: z.number(),
  totalParticipantCount: z.number(),
  averageParticipantCount: z.number(),
  averageRating: z.number().nullable(),
  netPromoterScore: z.number().nullable(),
  netProfit: z.number(),
  totalHostEarning: z.number(),
  totalEarned: z.number(),
  futurePayout: z.number(),
});

export const BrandsReviewSummaryResponseSchema = paginateResponseSchema(
  z.object({
    host: MinimalHostSchema.optional(),
    brand: BrandSchema.pick({ id: true, name: true }).extend({ logo: IMediaReferenceSchema.nullable() }).optional(),
    experience: z.object({ id: z.string(), title: z.string(), coverPicture: IMediaReferenceSchema.nullable() }).optional(),
    resourceGroup: z
      .object({ id: z.string(), title: z.string(), coverPicture: IMediaReferenceSchema.nullable() })
      .optional(),
    totalReviews: z.number(),
    averageRating: z.number(),
    npsScore: z.number().nullable(),
    reviewPercentage: z.number(),
  }),
).strict();

export const OrganizationFeatureFlagsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/features`,
  responseSchema: OrganizationFeatureFlagsResponseSchema,
  requestSchema: z.void(),
});

export const NestedOrganizationMembershipsOfUserResponseSchema = z.object({
  id: z.string(),
  membershipOrganizationId: z.string(),
  permissions: z.array(z.string()),
});

export type NestedOrganizationMembershipsOfUserResponse = z.infer<typeof NestedOrganizationMembershipsOfUserResponseSchema>;

export type IGetNestedOrganizationMembershipsOfUserEndpoint = IEndpointSchema<
  typeof GetNestedOrganizationMembershipsOfUserResponseSchemaEndpoint
>;

export const GetNestedOrganizationMembershipsOfUserResponseSchemaEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `v1/organizations/me/memberships`,
  responseSchema: z.array(NestedOrganizationMembershipsOfUserResponseSchema),
  requestSchema: z.void(),
});

export const GetOrganizationPromoterBrandsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/promoter-brands`,
  responseSchema: z.array(BrandSchema),
  requestSchema: z.void(),
});

const UpdatePromoterAgreementApprovalResponseSchema = z.object({
  id: z.string().uuid(),
});

const UpdatePromoterAgreementApprovalSchema = z.object({
  status: z.nativeEnum(PromoterAgreementStatus),
  updatedByUserId: z.string().uuid(),
});

export const UpdatePromoterAgreementApprovalEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ organizationId, agreementId }: { organizationId: string; agreementId: string }) =>
    `v1/organizations/${organizationId}/promoter-agreement/${agreementId}/update-approval`,
  responseSchema: UpdatePromoterAgreementApprovalResponseSchema,
  requestSchema: UpdatePromoterAgreementApprovalSchema,
});

const GetActiveOrganizationAgreementEndpointResponseSchema = z.array(
  z.object({
    id: z.string().uuid(),
  }),
);

export const GetActiveOrganizationAgreementEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId, promoterBrandId }: { organizationId: string; promoterBrandId: string }) =>
    `v1/organizations/${organizationId}/promoter-brands/${promoterBrandId}/promoter-agreement`,
  responseSchema: GetActiveOrganizationAgreementEndpointResponseSchema,
  requestSchema: z.void(),
});

export const ExperiencePillarItemSchema = z.object({
  name: z.nativeEnum(ExperiencePillar),
  description: z.string(),
  selected: z.boolean(),
});

export const BrandToneItemSchema = z.object({
  name: z.nativeEnum(BrandTone),
  description: z.string(),
  selected: z.boolean(),
});

export const GetOrganizationBrandIdentityResponseSchema = z.object({
  experiencePillars: z.array(ExperiencePillarItemSchema),
  brandTone: z.array(BrandToneItemSchema),
});

export type GetOrganizationBrandIdentityResponse = z.infer<typeof GetOrganizationBrandIdentityResponseSchema>;

export const UpdateOrganizationBrandIdentityRequestSchema = z.object({
  experiencePillars: z.array(z.nativeEnum(ExperiencePillar)),
  brandTone: z.array(z.nativeEnum(BrandTone)),
});

export type UpdateOrganizationBrandIdentityRequest = z.infer<typeof UpdateOrganizationBrandIdentityRequestSchema>;

export const GetOrganizationBrandIdentityEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/brand-identity`,
  responseSchema: GetOrganizationBrandIdentityResponseSchema,
  requestSchema: z.void(),
});

export const UpdateOrganizationBrandIdentityEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/brand-identity`,
  responseSchema: z.void(),
  requestSchema: UpdateOrganizationBrandIdentityRequestSchema,
});
